import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import CanonicalUploader from "@/components/documents/CanonicalUploader";

/**
 * LockedUploadDialog — the canonical uploader for context-bound surfaces.
 * The destination is INHERITED and shown as a locked label (staff never
 * re-selects the client). Secondary context (engagement / organizer / question /
 * document request) is passed to the backend, which validates cross-client
 * consistency server-side. Reuses CanonicalUploader for the upload experience.
 */
export function LockedUploadDialog({
  open, onClose, onDone,
  clientId, clientName,
  engagementId, engagementLabel,
  organizerId, questionCode,
  requestId, requestLabel,
  category = "other",
  title = "Upload Documents",
}) {
  const engagementPrimary = !clientId && engagementId;
  const navigate = useNavigate();

  const lockedLabel = requestLabel
    ? `For request: ${requestLabel}`
    : engagementPrimary
      ? `Uploading to engagement ${engagementLabel || engagementId}`
      : `Uploading to ${clientName || "this client"}`;

  const uploadFn = useCallback((file, { signal, asVersion, separate, note, onProgress }) => {
    const form = new FormData();
    form.append("file", file);
    form.append("category", category);
    if (engagementPrimary) {
      form.append("destination_type", "engagement");
      form.append("destination_id", engagementId);
    } else {
      form.append("destination_type", "client");
      form.append("destination_id", clientId);
      if (engagementId) form.append("engagement_id", engagementId);
    }
    if (requestId) form.append("request_id", requestId);
    if (organizerId) form.append("organizer_id", organizerId);
    if (questionCode) form.append("question_code", questionCode);
    if (asVersion) form.append("as_version", "true");
    if (separate) form.append("separate", "true");
    if (note) form.append("version_note", note);
    return api.post("/documents-intel/upload", form, {
      headers: { "Content-Type": "multipart/form-data" }, signal,
      onUploadProgress: (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
    }).then((r) => r.data).catch((err) => {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "duplicate_document") {
        const e = new Error("This file was already uploaded — keep as a new version?");
        e.duplicate = detail.existing; throw e;
      }
      throw new Error(formatApiErrorDetail(detail) || "Upload failed.");
    });
  }, [category, engagementPrimary, engagementId, clientId, requestId, organizerId, questionCode]);

  const pollStatus = useCallback((id) => api.get(`/documents-intel/${id}/status`).then((r) => r.data), []);

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="locked-upload-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">Drag & drop or browse to upload files to the inherited destination.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2 text-sm text-blue-800" data-testid="locked-destination-label">
            <Icons.Lock className="h-3.5 w-3.5 shrink-0" />
            <span className="font-medium truncate">{lockedLabel}</span>
          </div>
          <CanonicalUploader
            uploadFn={uploadFn}
            onAllComplete={() => onDone && onDone()}
            pollStatus={pollStatus}
            onView={(id) => navigate(`/documents/${id}`)}
            testId="locked-uploader"
          />
        </div>
        <DialogFooter>
          <button onClick={onClose} data-testid="locked-upload-close" className="text-sm px-4 py-2 rounded-lg bg-slate-900 text-white font-medium">Done</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default LockedUploadDialog;
